/**
 * Loading — **a skeleton in the shape of the final layout.** Never a spinner
 * over an empty page: a spinner tells the operator nothing about what is
 * coming, and the shape does.
 */
export function Skeleton({
  className = '',
  rounded = 'rounded',
}: {
  className?: string;
  rounded?: string;
}) {
  return (
    <div aria-hidden="true" className={`animate-pulse bg-pv-pale/70 ${rounded} ${className}`} />
  );
}

/** A block of lines whose widths taper, the way real text does. */
export function SkeletonLines({
  lines = 4,
  className = '',
}: {
  lines?: number;
  className?: string;
}) {
  const widths = ['w-full', 'w-4/5', 'w-full', 'w-3/5', 'w-11/12', 'w-2/3'];
  return (
    <div className={`space-y-3 ${className}`}>
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton key={index} className={`h-3.5 ${widths[index % widths.length]}`} />
      ))}
    </div>
  );
}

/** The shape of a counter strip, so the layout does not jump when it loads. */
export function SkeletonCounters({ count = 5 }: { count?: number }) {
  return (
    <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))` }}>
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="pv-card p-4">
          <Skeleton className="mb-3 h-3 w-24" />
          <Skeleton className="mb-2 h-8 w-20" />
          <Skeleton className="h-3 w-28" />
        </div>
      ))}
    </div>
  );
}

/** The shape of a table: a header band and rows of the right height. */
export function SkeletonTable({ rows = 8, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div className="overflow-hidden pv-card" role="status" aria-label="Loading table">
      <div className="flex gap-4 border-b border-pv-line bg-pv-primary/5 px-4 py-2.5">
        {Array.from({ length: columns }, (_, index) => (
          <Skeleton key={index} className="h-3 flex-1" />
        ))}
      </div>
      {Array.from({ length: rows }, (_, rowIndex) => (
        <div
          key={rowIndex}
          className="flex gap-4 border-b border-pv-line/60 px-4 py-2.5 last:border-0"
        >
          {Array.from({ length: columns }, (_, index) => (
            <Skeleton key={index} className="h-3 flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}
